import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiDownload,
    FiFilter,
    FiInfo,
    FiMail,
    FiMinusCircle,
    FiShield,
    FiUsers,
    FiXCircle,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useTranslation } from '../../hooks/useTranslation';
import { getEmailListItemLabel } from '../../constants/emailListItems';
import { formatSubscriptionEmailReason } from '../../utils/tripsPoliciesImportResult';
import {
    END_OF_STAY_RECAP_CODES,
    TRIGGERED_RECAP_CODES,
    buildTriggeredRecapExcelRows,
    formatTriggeredRecapDate,
    getTriggeredRecapAlertVariant,
    getTriggeredRecapRowStatusBadgeVariant,
    isRecapSuccessCode,
    normalizeTriggeredRecapResult,
} from '../../utils/triggeredRecapResult';
import './TriggeredRecapResult.css';

const StatCard = ({ icon: Icon, tone, label, value }) => (
    <Card className="triggered-recap-stat-card">
        <Card.Body className="d-flex align-items-center gap-3">
            <span className={`triggered-recap-stat-icon ${tone}`} aria-hidden>
                <Icon />
            </span>
            <div>
                <div className="text-muted small">{label}</div>
                <div className="triggered-recap-stat-value">{value}</div>
            </div>
        </Card.Body>
    </Card>
);

const formatDetailReason = (reason) => formatSubscriptionEmailReason(reason, getEmailListItemLabel);

const TriggeredRecapResult = ({ result, onDismiss, recapKind = 'flower' }) => {
    const { t } = useTranslation();
    const [exportError, setExportError] = useState(null);
    const prefix = recapKind === 'standard'
        ? 'pages.adminTripsDaily.standardEndOfStayRecap'
        : recapKind === 'endOfStay'
            ? 'pages.adminTripsDaily.endOfStayRecap'
            : 'pages.adminTripsDaily.triggeredRecap';

    const normalized = useMemo(() => normalizeTriggeredRecapResult(result), [result]);
    if (!normalized) return null;

    const { code, message, timestamp, summary, filters, details, error, success } = normalized;
    const variant = getTriggeredRecapAlertVariant(normalized);
    const hasSummary = isRecapSuccessCode(code) || code === TRIGGERED_RECAP_CODES.NO_CLIENTS;
    const showExtendedSummary = (recapKind === 'endOfStay' || recapKind === 'standard')
        && (summary.emailsTriggered != null || summary.emailsNotTriggered != null);
    const showFlowerDetails = recapKind === 'flower' && details.length > 0;
    const showStandardDetails = recapKind === 'standard' && details.length > 0;
    const showSkippedStats = recapKind === 'flower' || recapKind === 'standard';
    const allSkippedNoSent = summary.emailsSent === 0
        && (summary.emailsSkipped > 0 || summary.emailsSkippedEmailList > 0);

    const BannerIcon = variant === 'success' ? FiCheckCircle
        : variant === 'info' ? FiInfo
            : variant === 'warning' ? FiAlertTriangle
                : FiXCircle;

    const codeTitleKey = `${prefix}.codes.${code}`;
    const codeTitle = t(codeTitleKey);
    const displayTitle = codeTitle !== codeTitleKey ? codeTitle : code;

    const getFieldLabel = (field) => {
        const fieldKey = `${prefix}.fields.${field}`;
        const label = t(fieldKey);
        return label !== fieldKey ? label : field;
    };

    const translateRowStatus = (status) => {
        const key = `${prefix}.rowStatus.${status}`;
        const label = t(key);
        return label !== key ? label : (status || '—');
    };

    const handleExportExcel = () => {
        if (details.length === 0) {
            setExportError(t(`${prefix}.exportNoData`));
            return;
        }

        setExportError(null);
        const rows = buildTriggeredRecapExcelRows(details, {
            labels: {
                policyCode: t(`${prefix}.details.policyCode`),
                reservationNumber: t(`${prefix}.details.reservationNumber`),
                client: t(`${prefix}.details.client`),
                email: t(`${prefix}.details.email`),
                companyName: t(`${prefix}.details.companyName`),
                tripEndDate: t(`${prefix}.details.tripEndDate`),
                claimTotal: t(`${prefix}.details.claimTotal`),
                cashTotal: t(`${prefix}.details.cashTotal`),
                voucherTotal: t(`${prefix}.details.voucherTotal`),
                status: t(`${prefix}.details.status`),
                emailCode: t(`${prefix}.details.emailCode`),
                triggered: t(`${prefix}.details.triggered`),
                partnerId: t(`${prefix}.details.partnerId`),
                message: t(`${prefix}.details.message`),
            },
            translateStatus: translateRowStatus,
            formatReason: formatDetailReason,
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Recap indemnisations');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
        XLSX.writeFile(workbook, `triggered_recap_${stamp}.xlsx`);
    };

    return (
        <div className="triggered-recap-result">
            <Alert variant={variant} className="triggered-recap-banner" dismissible={!!onDismiss} onClose={onDismiss}>
                <div className="d-flex align-items-start gap-2">
                    <BannerIcon size={22} className="mt-1 flex-shrink-0" aria-hidden />
                    <div className="flex-grow-1">
                        <Alert.Heading className="h5 mb-1">{displayTitle}</Alert.Heading>
                        {message && <p className="mb-2">{message}</p>}
                        {(code === TRIGGERED_RECAP_CODES.SENT || code === END_OF_STAY_RECAP_CODES.SENT) && summary.emailsFailed > 0 && (
                            <p className="mb-2 small">
                                <Badge bg="warning" text="dark">{t(`${prefix}.partialWarning`)}</Badge>
                            </p>
                        )}
                        {(code === TRIGGERED_RECAP_CODES.SENT || code === END_OF_STAY_RECAP_CODES.SENT) && allSkippedNoSent && (
                            <p className="mb-2 small">
                                <Badge bg="warning" text="dark">{t(`${prefix}.skippedEmailListWarning`)}</Badge>
                            </p>
                        )}
                        {timestamp && (
                            <p className="mb-0 small text-muted">
                                {t(`${prefix}.timestamp`)} : {new Date(timestamp).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                            </p>
                        )}
                    </div>
                </div>
            </Alert>

            {!success && error && (
                <Alert variant={code === TRIGGERED_RECAP_CODES.INVALID ? 'warning' : 'danger'} className="mt-3">
                    {code === TRIGGERED_RECAP_CODES.INVALID && error.field && (
                        <p className="mb-1">
                            <strong>{getFieldLabel(error.field)}</strong>
                            {' — '}
                            {error.reason}
                        </p>
                    )}
                    {code === TRIGGERED_RECAP_CODES.COMPANY_NOT_FOUND && error.company_name && (
                        <p className="mb-1">
                            <strong>{t(`${prefix}.companyNotFound`)}</strong> {error.company_name}
                        </p>
                    )}
                    {code === TRIGGERED_RECAP_CODES.INTERNAL && error.details && (
                        <p className="mb-0 triggered-recap-debug" title={error.details}>
                            {t(`${prefix}.technicalHint`)} <code>{error.type || '—'}</code>
                        </p>
                    )}
                </Alert>
            )}

            {(filters.tripEndDateFrom || filters.tripEndDateTo || filters.companyName) && (
                <Card className="mb-3 mt-3">
                    <Card.Header className="d-flex align-items-center gap-2 bg-white">
                        <FiFilter />
                        <span className="fw-semibold">{t(`${prefix}.filtersTitle`)}</span>
                    </Card.Header>
                    <Card.Body>
                        <dl className="triggered-recap-filters-grid mb-0">
                            <div className="triggered-recap-filter-item">
                                <dt>{t(`${prefix}.filterDateFrom`)}</dt>
                                <dd>{formatTriggeredRecapDate(filters.tripEndDateFrom)}</dd>
                            </div>
                            <div className="triggered-recap-filter-item">
                                <dt>{t(`${prefix}.filterDateTo`)}</dt>
                                <dd>{formatTriggeredRecapDate(filters.tripEndDateTo)}</dd>
                            </div>
                            <div className="triggered-recap-filter-item">
                                <dt>{t(`${prefix}.filterCompany`)}</dt>
                                <dd>{filters.companyName || t(`${prefix}.allPartners`)}</dd>
                            </div>
                        </dl>
                    </Card.Body>
                </Card>
            )}

            {hasSummary && (
                <Row className="g-3">
                    <Col xs={12} sm={6} xl={showSkippedStats ? 3 : 4}>
                        <StatCard
                            icon={FiUsers}
                            tone="primary"
                            label={t(`${prefix}.summary.eligibleClients`)}
                            value={summary.eligibleClients}
                        />
                    </Col>
                    <Col xs={12} sm={6} xl={showSkippedStats ? 3 : 4}>
                        <StatCard
                            icon={FiMail}
                            tone="success"
                            label={t(`${prefix}.summary.emailsSent`)}
                            value={summary.emailsSent}
                        />
                    </Col>
                    {showSkippedStats && (
                        <Col xs={12} sm={6} xl={3}>
                            <StatCard
                                icon={FiMinusCircle}
                                tone="warning"
                                label={t(`${prefix}.summary.emailsSkipped`)}
                                value={summary.emailsSkipped}
                            />
                        </Col>
                    )}
                    <Col xs={12} sm={6} xl={showSkippedStats ? 3 : 4}>
                        <StatCard
                            icon={FiXCircle}
                            tone="danger"
                            label={t(`${prefix}.summary.emailsFailed`)}
                            value={summary.emailsFailed}
                        />
                    </Col>
                </Row>
            )}

            {hasSummary && recapKind === 'standard' && summary.emailsSkippedEmailList > 0 && (
                <Row className="g-3 mt-1">
                    <Col xs={12} sm={6}>
                        <StatCard
                            icon={FiMinusCircle}
                            tone="warning"
                            label={t(`${prefix}.summary.emailsSkippedEmailList`)}
                            value={summary.emailsSkippedEmailList}
                        />
                    </Col>
                </Row>
            )}

            {hasSummary && showExtendedSummary && (
                <Row className="g-3 mt-1">
                    <Col xs={12} sm={6}>
                        <StatCard
                            icon={FiShield}
                            tone="primary"
                            label={t(`${prefix}.summary.emailsTriggered`)}
                            value={summary.emailsTriggered ?? 0}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <StatCard
                            icon={FiMail}
                            tone="success"
                            label={t(`${prefix}.summary.emailsNotTriggered`)}
                            value={summary.emailsNotTriggered ?? 0}
                        />
                    </Col>
                </Row>
            )}

            {showFlowerDetails && (
                <Card className="mt-4">
                    <Card.Header className="bg-white fw-semibold d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>{t(`${prefix}.detailsTitle`)}</span>
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleExportExcel}
                        >
                            <FiDownload className="me-2" />
                            {t(`${prefix}.exportExcel`)}
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {exportError && (
                            <Alert variant="warning" className="mb-3" dismissible onClose={() => setExportError(null)}>
                                {exportError}
                            </Alert>
                        )}
                        <div className="triggered-recap-table-wrap">
                            <Table striped hover responsive className="triggered-recap-table mb-0">
                                <thead>
                                    <tr>
                                        <th>{t(`${prefix}.details.policyCode`)}</th>
                                        <th>{t(`${prefix}.details.reservationNumber`)}</th>
                                        <th>{t(`${prefix}.details.client`)}</th>
                                        <th>{t(`${prefix}.details.email`)}</th>
                                        <th>{t(`${prefix}.details.claimTotal`)}</th>
                                        <th>{t(`${prefix}.details.status`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.map((row, index) => {
                                        const clientName = `${row.clientInfo?.firstName || ''} ${row.clientInfo?.lastName || ''}`.trim();
                                        const rowKey = [
                                            row.policyInfo?.policyId,
                                            row.activityInfo?.reservationNumber,
                                            row.clientInfo?.email,
                                            index,
                                        ].filter(Boolean).join('-');

                                        return (
                                            <tr key={rowKey}>
                                                <td>{row.policyInfo?.policyCode || '—'}</td>
                                                <td>{row.activityInfo?.reservationNumber || '—'}</td>
                                                <td>{clientName || '—'}</td>
                                                <td>{row.clientInfo?.email || '—'}</td>
                                                <td>{row.amountsSent?.claimTotal || '—'}</td>
                                                <td>
                                                    <Badge bg={getTriggeredRecapRowStatusBadgeVariant(row.status)}>
                                                        {translateRowStatus(row.status)}
                                                    </Badge>
                                                    {row.message && (
                                                        <div className="small text-muted mt-1">{row.message}</div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {showStandardDetails && (
                <Card className="mt-4">
                    <Card.Header className="bg-white fw-semibold d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>{t(`${prefix}.detailsTitle`)}</span>
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleExportExcel}
                        >
                            <FiDownload className="me-2" />
                            {t(`${prefix}.exportExcel`)}
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {exportError && (
                            <Alert variant="warning" className="mb-3" dismissible onClose={() => setExportError(null)}>
                                {exportError}
                            </Alert>
                        )}
                        <div className="triggered-recap-table-wrap">
                            <Table striped hover responsive className="triggered-recap-table mb-0">
                                <thead>
                                    <tr>
                                        <th>{t(`${prefix}.details.policyCode`)}</th>
                                        <th>{t(`${prefix}.details.status`)}</th>
                                        <th>{t(`${prefix}.details.emailCode`)}</th>
                                        <th>{t(`${prefix}.details.triggered`)}</th>
                                        <th>{t(`${prefix}.details.message`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.map((row, index) => {
                                        const rowKey = [
                                            row.policyInfo?.policyCode,
                                            row.emailCode,
                                            row.partnerId,
                                            index,
                                        ].filter(Boolean).join('-');
                                        const reasonText = formatDetailReason(row.reason || row.message);

                                        return (
                                            <tr key={rowKey}>
                                                <td><code>{row.policyInfo?.policyCode || '—'}</code></td>
                                                <td>
                                                    <Badge bg={getTriggeredRecapRowStatusBadgeVariant(row.status)}>
                                                        {translateRowStatus(row.status)}
                                                    </Badge>
                                                </td>
                                                <td className="small">
                                                    {row.emailCode
                                                        ? getEmailListItemLabel(row.emailCode)
                                                        : '—'}
                                                    {row.emailCode && (
                                                        <div className="text-muted"><code>{row.emailCode}</code></div>
                                                    )}
                                                </td>
                                                <td>
                                                    {row.triggered == null
                                                        ? '—'
                                                        : (row.triggered
                                                            ? t(`${prefix}.details.triggeredYes`)
                                                            : t(`${prefix}.details.triggeredNo`))}
                                                </td>
                                                <td className="small text-break">{reasonText}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default TriggeredRecapResult;
